/**
 * 收款入口 MSW 桩（payment-entries-links-and-qr:ADM-10 开发态 Mock）。
 *
 * admin-web 复用 MSW handler（design D1：随 worker 默认启用，无 VITE_USE_MOCK 值开关分支）。
 * 桩覆盖：生成（幂等返回既有有效入口，SC-05）、按订单列表、详情、修改分期授权
 * （尾款/终态拒 409，SC-09/10）、转发审计。
 *
 * 字段权威：`prototype/shared/src/types/domain.ts:436-473` + `src/types/payment.types.ts`。
 * token 形态对齐 SC-11（base64url ≥32 字符，192-bit 不可猜测）；guardianMobileHash 为 sha256 hex 占位。
 */
import { http, HttpResponse } from 'msw';
import type {
  CreatePaymentEntryPayload,
  PaymentEntryType,
  PaymentEntryView,
  UpdateAllowDepositPayload,
} from '../../types/payment.types';

/** 桩固定 UUID（合法格式，对齐 handlers.ts 桩常量风格）。 */
const MOCK_CAMPUS_ID = '00000000-0000-4000-8000-0000000000a1';
const MOCK_STUDENT_ID = '00000000-0000-4000-8000-0000000000s1';
const MOCK_STAFF_ID = '00000000-0000-1000-8000-000000000001';
const MOCK_ORDER_ID = '00000000-0000-4000-8000-0000000000o1';
const MOCK_PLAN_ID = '00000000-0000-4000-8000-0000000000p1';

/** base64url ≥32 字符令牌占位（满足 SC-11 192-bit 形态校验，mock 自洽）。 */
const MOCK_TOKEN_QR = 'mock-token-qr-aB3dEf6gHi9kLm2nOpQrStUvWxYz4AbC';
const MOCK_TOKEN_LINK = 'mock-token-link-cDeFgHiJkLmNoPqRsTuVwXyZ0aBcD5eF';

/** IKBFM3：mock 通道下二维码字段与模拟支付（服务端下发内容的前端桩）。 */
function withQrFields(entry: PaymentEntryView): PaymentEntryView {
  return {
    ...entry,
    paymentChannel: 'mock',
    qrContent: `https://tcms.example.com/pay/${entry.token}`,
    qrImageUrl: `/api/payment-entries/${entry.token}/qr`,
  };
}

/** 入口视图桩（含读时派生字段，对齐 PaymentEntryView）。 */
const mockPaymentEntries: PaymentEntryView[] = [
  {
    id: '00000000-0000-4000-8000-0000000000e1',
    orderId: MOCK_ORDER_ID,
    receivablePlanId: undefined,
    studentId: MOCK_STUDENT_ID,
    entryType: 'QR',
    amount: undefined,
    allowDeposit: false,
    guardianMobileHash: 'a'.repeat(64),
    token: MOCK_TOKEN_QR,
    createdByStaffId: MOCK_STAFF_ID,
    campusId: MOCK_CAMPUS_ID,
    status: 'WAITING_SCAN',
    createdAt: '2026-08-20T10:00:00.000Z',
    openCount: 0,
    derivedStatus: 'WAITING_SCAN',
    validUntil: '2026-08-20T10:30:00.000Z',
    createdByName: '张三',
    studentName: '小*',
    campusName: '高新校区',
  },
  {
    id: '00000000-0000-4000-8000-0000000000e2',
    orderId: MOCK_ORDER_ID,
    receivablePlanId: MOCK_PLAN_ID,
    studentId: MOCK_STUDENT_ID,
    entryType: 'LINK',
    amount: 480000,
    allowDeposit: false,
    guardianMobileHash: 'a'.repeat(64),
    token: MOCK_TOKEN_LINK,
    createdByStaffId: MOCK_STAFF_ID,
    campusId: MOCK_CAMPUS_ID,
    status: 'WAITING_PAYMENT',
    createdAt: '2026-08-20T09:30:00.000Z',
    openCount: 2,
    lastOpenedAt: '2026-08-20T09:35:00.000Z',
    derivedStatus: 'WAITING_PAYMENT',
    validUntil: '2026-08-20T10:00:00.000Z',
    createdByName: '张三',
    studentName: '小*',
    campusName: '高新校区',
    receivablePlanSummary: {
      receivablePlanId: MOCK_PLAN_ID,
      type: 'FINAL',
      amount: 480000,
      paidAmount: 0,
      dueAt: '2026-09-20T00:00:00.000Z',
      status: 'PENDING',
    },
  },
];

/** 查找有效入口（WAITING_SCAN/WAITING_PAYMENT）用于幂等判定（SC-05）。 */
function findActiveEntry(
  orderId: string,
  receivablePlanId: string | undefined,
  entryType: PaymentEntryType,
): PaymentEntryView | undefined {
  return mockPaymentEntries.find(
    (e) =>
      e.orderId === orderId &&
      e.entryType === entryType &&
      (e.receivablePlanId ?? '') === (receivablePlanId ?? '') &&
      (e.status === 'WAITING_SCAN' || e.status === 'WAITING_PAYMENT'),
  );
}

export const paymentEntryHandlers = [
  /** 生成入口（SC-01/05：无金额字段；同订单同节点同类型有效入口幂等返回既有）。 */
  http.post('/api/payment-entries', async ({ request }) => {
    const body = (await request.json().catch(() => ({}))) as CreatePaymentEntryPayload;
    const existing = findActiveEntry(
      body.orderId,
      body.receivablePlanId,
      body.entryType,
    );
    if (existing) {
      return HttpResponse.json({
        success: true,
        data: withQrFields(existing),
        timestamp: 't',
      });
    }
    const created: PaymentEntryView = {
      id: crypto.randomUUID(),
      orderId: body.orderId,
      receivablePlanId: body.receivablePlanId,
      studentId: MOCK_STUDENT_ID,
      entryType: body.entryType,
      amount: body.receivablePlanId ? 480000 : undefined,
      allowDeposit: body.allowDeposit ?? false,
      guardianMobileHash: 'b'.repeat(64),
      token: `mock-token-${body.entryType.toLowerCase()}-${Math.random()
        .toString(36)
        .slice(2)
        .padEnd(26, '0')
        .slice(0, 26)}`.padEnd(32, '0'),
      createdByStaffId: MOCK_STAFF_ID,
      campusId: MOCK_CAMPUS_ID,
      status: 'WAITING_SCAN',
      createdAt: new Date().toISOString(),
      openCount: 0,
      derivedStatus: 'WAITING_SCAN',
      validUntil: new Date(Date.now() + 30 * 60 * 1000).toISOString(),
      createdByName: '张三',
      studentName: '小*',
      campusName: '高新校区',
    };
    mockPaymentEntries.push(created);
    return HttpResponse.json(
      { success: true, data: withQrFields(created), timestamp: 't' },
      { status: 201 },
    );
  }),

  /** 按订单列入口（管理端对话框 / 员工端查看）。 */
  http.get('/api/payment-entries', ({ request }) => {
    const url = new URL(request.url);
    const orderId = url.searchParams.get('orderId') ?? '';
    const items = mockPaymentEntries.filter((e) => e.orderId === orderId);
    return HttpResponse.json({
      success: true,
      data: items.map(withQrFields),
      timestamp: 't',
    });
  }),

  /** 入口详情（派生态实时）。 */
  http.get('/api/payment-entries/:id', ({ params }) => {
    const entry = mockPaymentEntries.find((e) => e.id === params.id);
    if (!entry) {
      return HttpResponse.json(
        {
          success: false,
          error: { code: 'billing.entry_not_found', message: '收款入口不存在' },
          timestamp: 't',
        },
        { status: 404 },
      );
    }
    return HttpResponse.json({ success: true, data: withQrFields(entry), timestamp: 't' });
  }),

  /** 模拟家长已支付（IKBFM3 mock 通道：入口置 PAID，幂等）。 */
  http.post('/api/payment-entries/:id/mock-paid', ({ params }) => {
    const entry = mockPaymentEntries.find((e) => e.id === params.id);
    if (!entry) {
      return HttpResponse.json(
        {
          success: false,
          error: { code: 'billing.entry_not_found', message: '收款入口不存在' },
          timestamp: 't',
        },
        { status: 404 },
      );
    }
    const idempotent = entry.status === 'PAID';
    if (entry.status !== 'PAID' && entry.status !== 'CLOSED') {
      entry.status = 'PAID';
      entry.derivedStatus = 'PAID';
    }
    return HttpResponse.json({
      success: true,
      data: {
        entryId: entry.id,
        orderId: entry.orderId,
        status: entry.status === 'PAID' ? 'PAID' : 'FAILED',
        businessPaymentNo: `WXP-MOCK-${entry.id.slice(0, 8)}`,
        paymentTransactionId: idempotent ? null : crypto.randomUUID(),
        idempotent,
        failure: null,
      },
      timestamp: 't',
    });
  }),

  /** 收款二维码 PNG（IKBFM3：1x1 透明 PNG 占位，满足 <img> 加载）。 */
  http.get('/api/payment-entries/:token/qr', () =>
    HttpResponse.arrayBuffer(
      Uint8Array.from(
        atob(
          'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==',
        ),
        (c) => c.charCodeAt(0),
      ).buffer,
      { headers: { 'Content-Type': 'image/png' } },
    ),
  ),

  /** 修改分期授权（SC-08/09/10：仅首笔未支付可改；尾款恒 false；终态拒）。 */
  http.post('/api/payment-entries/:id/allow-deposit', async ({ params, request }) => {
    const entry = mockPaymentEntries.find((e) => e.id === params.id);
    if (!entry) {
      return HttpResponse.json(
        {
          success: false,
          error: { code: 'billing.entry_not_found', message: '收款入口不存在' },
          timestamp: 't',
        },
        { status: 404 },
      );
    }
    if (entry.receivablePlanId) {
      return HttpResponse.json(
        {
          success: false,
          error: {
            code: 'billing.entry_deposit_not_modifiable',
            message: '尾款入口授权恒为 false，不可修改',
          },
          timestamp: 't',
        },
        { status: 409 },
      );
    }
    if (entry.status === 'PAID' || entry.status === 'CLOSED') {
      return HttpResponse.json(
        {
          success: false,
          error: {
            code: 'billing.entry_invalid_transition',
            message: '入口已进入终态，不可修改授权',
          },
          timestamp: 't',
        },
        { status: 409 },
      );
    }
    const body = (await request.json().catch(() => ({}))) as UpdateAllowDepositPayload;
    entry.allowDeposit = body.allowDeposit;
    return HttpResponse.json({ success: true, data: entry, timestamp: 't' });
  }),

  /** 转发审计（SC-22：员工「发送微信给家长」点击记审计）。 */
  http.post('/api/payment-entries/:id/forward-audit', ({ params }) => {
    const entry = mockPaymentEntries.find((e) => e.id === params.id);
    if (!entry) {
      return HttpResponse.json(
        {
          success: false,
          error: { code: 'billing.entry_not_found', message: '收款入口不存在' },
          timestamp: 't',
        },
        { status: 404 },
      );
    }
    return HttpResponse.json({
      success: true,
      data: { entryId: entry.id },
      timestamp: 't',
    });
  }),
];
